/**
 * The "Around the area" digest: cached place surveys → category chips with
 * count and median rating.
 *
 * The cache commonly returns the SAME place from several radius queries, so
 * dedupe by place identity comes first — counting rows would double-count
 * places and inflate every chip. A place with no identity falls back to its
 * name; a place with neither is skipped (it cannot be deduped, and a count
 * built on un-dedupable rows is the inflation we are avoiding).
 *
 * No coordinates are read here — the map slot is deliberately empty in v1
 * (AREA_SCORING_DESIGN §7/§11: a blank tile is worse than no map).
 */
import { CATEGORY_ORDER, categoryFor, type CategoryKey } from './copy'

export interface CachedSurveyRow {
  query_category?: string | null
  results_json?: string | null
}

export interface PlaceEntry {
  place_id?: string | null
  name?: string | null
  rating?: number | null
}

export interface PlaceCategoryDigest {
  category: CategoryKey
  count: number
  medianRating: number | null
}

function parsePlaces(row: CachedSurveyRow): PlaceEntry[] {
  if (!row.results_json) return []
  try {
    const parsed = JSON.parse(row.results_json)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function identityOf(place: PlaceEntry): string | null {
  if (place.place_id) return `id:${place.place_id}`
  if (place.name) return `name:${place.name}`
  return null
}

function median(values: number[]): number | null {
  if (!values.length) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

/** Count + median rating per category, in the spec's fixed chip order. */
export function digestPlaces(rows: CachedSurveyRow[]): PlaceCategoryDigest[] {
  const byIdentity = new Map<string, { category: CategoryKey; rating: number | null }>()

  for (const row of rows) {
    const category = categoryFor(row.query_category)
    for (const place of parsePlaces(row)) {
      const identity = identityOf(place)
      if (!identity) continue
      // First sighting wins: same place, same facts. A later radius row
      // carrying a different rating for the same place is cache drift, not a
      // second place, and the digest is about identity, not freshness.
      if (!byIdentity.has(identity)) {
        byIdentity.set(identity, {
          category,
          rating: typeof place.rating === 'number' && Number.isFinite(place.rating) ? place.rating : null,
        })
      }
    }
  }

  const buckets = new Map<CategoryKey, number[]>()
  for (const entry of byIdentity.values()) {
    const list = buckets.get(entry.category) || []
    if (entry.rating != null) list.push(entry.rating)
    buckets.set(entry.category, list)
  }

  const counts = new Map<CategoryKey, number>()
  for (const entry of byIdentity.values()) {
    counts.set(entry.category, (counts.get(entry.category) || 0) + 1)
  }

  return CATEGORY_ORDER
    .filter((category) => (counts.get(category) || 0) > 0)
    .map((category) => ({
      category,
      count: counts.get(category) || 0,
      medianRating: median(buckets.get(category) || []),
    }))
}
